/**
 * V4.24.50 — Outsource Partner Picker (Type B — b_outsource).
 *
 * After the customer/agent picks "b - Outsource agency" on the Score+Path
 * screen, they land here to actually choose WHICH outsource partner will
 * process their loan case.
 *
 * Backend:
 *   GET  /api/public/v1/outsource-partners  → list of active partners
 *   POST /api/public/v1/loan-cases/:id/select-outsource { outsource_partner_id }
 *
 * Deep-link path: `/loan/:id/pick-outsource`.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2, Briefcase, Phone, Mail } from 'lucide-react';
import { LoanPageHeader } from './_shared';

export default function LoanPickOutsource() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get(`/public/v1/loan-cases/${id}`),
          api.get('/public/v1/outsource-partners?limit=50'),
        ]);
        setCaseData(c.data?.data || null);
        const items = p.data?.data?.items || p.data?.items || [];
        setPartners(items);
      } catch (e) { toast.error('Failed to load partners'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const submit = async () => {
    if (!picked) { toast.error('Pick a partner first'); return; }
    setSubmitting(true);
    try {
      await api.post(`/public/v1/loan-cases/${id}/select-outsource`, {
        outsource_partner_id: picked,
      });
      toast.success('Outsource partner assigned');
      navigate(`/loan/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to assign partner');
    } finally { setSubmitting(false); }
  };

  if (loading || !caseData) return <div className="p-6 text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto p-3 sm:p-6 space-y-4" data-testid="loan-pick-outsource-page">
      <LoanPageHeader caseData={caseData} title="Pick Outsource Partner" />
      <p className="text-sm text-gray-600">
        Choose an agency to handle your loan application on your behalf. The assigned partner will contact you directly to complete the paperwork.
      </p>

      {partners.length === 0 ? (
        <Card className="p-6 text-center text-gray-500" data-testid="no-partners-empty">
          No active outsource partners available right now. Please contact SAM Rumah support to assist you.
        </Card>
      ) : (
        <div className="space-y-2">
          {partners.map((p) => (
            <label
              key={p.id}
              className={`border rounded-lg p-3 flex items-start gap-3 cursor-pointer ${
                picked === p.id ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
              data-testid={`outsource-partner-${p.id}`}
            >
              <input
                type="radio"
                name="partner"
                checked={picked === p.id}
                onChange={() => setPicked(p.id)}
                className="mt-1"
                data-testid={`outsource-radio-${p.id}`}
              />
              <Briefcase className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-gray-800">{p.name}</p>
                {p.specialty && <p className="text-xs text-gray-500">{p.specialty}</p>}
                <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-600">
                  {p.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {p.contact_phone}</span>}
                  {p.contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {p.contact_email}</span>}
                </div>
              </div>
              {p.commission_rate != null && (
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700">{p.commission_rate}%</Badge>
              )}
            </label>
          ))}
        </div>
      )}

      {partners.length > 0 && (
        <div className="sticky bottom-0 bg-white border-t p-3 flex items-center justify-between" data-testid="outsource-submit-bar">
          <span className="text-sm text-gray-600">{picked ? '1 partner selected' : 'None selected'}</span>
          <Button onClick={submit} disabled={submitting || !picked} className="bg-emerald-600 hover:bg-emerald-700" data-testid="outsource-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Confirm Partner
          </Button>
        </div>
      )}
    </div>
  );
}
